import React, { useState, useEffect } from 'react';
import { 
    Search, Filter, Download, Users, Calendar, FileText, 
    Phone, Mail, MapPin, Clock, AlertTriangle, CheckCircle,
    ChevronDown, ChevronUp, X
} from 'lucide-react';
import { useAlert } from './contexts/AlertContext';
import { useAuth } from './contexts/AuthContext';
import apiService from './apiService';

const AdvancedPatientSearch = ({ onNavigate }) => {
    const { currentUser } = useAuth();
    const { showAlert } = useAlert();
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [totalResults, setTotalResults] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    
    // Search filters
    const [filters, setFilters] = useState({
        name: '',
        phone: '',
        email: '',
        ageMin: '',
        ageMax: '',
        department: '',
        status: '',
        dateFrom: '',
        dateTo: '',
        hasAppointments: '',
        hasMedicalRecords: '',
        symptoms: ''
    });
    
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');

    useEffect(() => {
        performSearch();
    }, [currentPage, sortBy, sortOrder]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            name: '',
            phone: '',
            email: '',
            ageMin: '',
            ageMax: '',
            department: '',
            status: '',
            dateFrom: '',
            dateTo: '',
            hasAppointments: '',
            hasMedicalRecords: '',
            symptoms: ''
        });
        setCurrentPage(1);
    };

    const performSearch = async () => {
        setLoading(true);
        try {
            // Build search parameters
            const searchParams = {
                organizationId: currentUser.organizationId,
                page: currentPage,
                limit: itemsPerPage,
                sortBy,
                sortOrder,
                ...filters
            };

            // Remove empty filters
            Object.keys(searchParams).forEach(key => {
                if (searchParams[key] === '' || searchParams[key] === null || searchParams[key] === undefined) {
                    delete searchParams[key];
                }
            });

            const patients = await apiService.getPatients(searchParams);
            setSearchResults(patients);
            setTotalResults(patients.length); // This would be total count from API in real implementation
        } catch (error) {
            console.error('Error searching patients:', error);
            showAlert('Failed to search patients', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setCurrentPage(1);
        performSearch();
    };

    const exportResults = () => {
        if (searchResults.length === 0) {
            showAlert('No results to export', 'warning');
            return;
        }

        const csvContent = convertToCSV(searchResults);
        downloadCSV(csvContent, `patient-search-${new Date().toISOString().split('T')[0]}.csv`);
        showAlert('Search results exported successfully', 'success');
    };

    const convertToCSV = (data) => {
        if (data.length === 0) return '';
        
        const headers = ['Name', 'Age', 'Phone', 'Email', 'Department', 'Status', 'Symptoms', 'Created Date'];
        const csvRows = [headers.join(',')];
        
        data.forEach(patient => {
            const values = [
                patient.name || '',
                patient.age || '',
                patient.phone || '',
                patient.email || '',
                patient.department || '',
                patient.status || '',
                patient.symptoms || '',
                new Date(patient.createdAt).toLocaleDateString()
            ];
            csvRows.push(values.map(v => `"${v.toString().replace(/"/g, '""')}"`).join(','));
        });
        
        return csvRows.join('\n');
    };

    const downloadCSV = (content, filename) => {
        const blob = new Blob([content], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'waiting': return 'bg-yellow-100 text-yellow-800';
            case 'in-progress': return 'bg-blue-100 text-blue-800';
            case 'completed': return 'bg-gray-100 text-gray-800';
            case 'archived': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const totalPages = Math.ceil(totalResults / itemsPerPage);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Advanced Patient Search</h2>
                    <p className="text-gray-600">Search and filter patients with advanced criteria</p>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        onClick={exportResults}
                        disabled={searchResults.length === 0}
                        className="bg-green-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Download className="w-4 h-4" />
                        Export ({searchResults.length})
                    </button>
                </div>
            </div>

            {/* Search Form */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Search Criteria</h3>
                    <button
                        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                    >
                        <Filter className="w-4 h-4" />
                        {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
                        {showAdvancedFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                </div>

                {/* Basic Search */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Patient Name
                        </label>
                        <input
                            type="text"
                            placeholder="Enter patient name..."
                            value={filters.name}
                            onChange={(e) => handleFilterChange('name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Phone Number
                        </label>
                        <input
                            type="tel"
                            placeholder="Enter phone number..."
                            value={filters.phone}
                            onChange={(e) => handleFilterChange('phone', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email
                        </label>
                        <input
                            type="email"
                            placeholder="Enter email address..."
                            value={filters.email}
                            onChange={(e) => handleFilterChange('email', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>

                {/* Advanced Filters */}
                {showAdvancedFilters && (
                    <div className="border-t pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Age Range
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="number"
                                        placeholder="Min"
                                        value={filters.ageMin}
                                        onChange={(e) => handleFilterChange('ageMin', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    <input
                                        type="number"
                                        placeholder="Max"
                                        value={filters.ageMax}
                                        onChange={(e) => handleFilterChange('ageMax', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Department
                                </label>
                                <select
                                    value={filters.department}
                                    onChange={(e) => handleFilterChange('department', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All Departments</option>
                                    <option value="general">General Medicine</option>
                                    <option value="cardiology">Cardiology</option>
                                    <option value="pediatrics">Pediatrics</option>
                                    <option value="orthopedics">Orthopedics</option>
                                    <option value="dermatology">Dermatology</option>
                                    <option value="emergency">Emergency</option>
                                </select>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Status
                                </label>
                                <select
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All Statuses</option>
                                    <option value="active">Active</option>
                                    <option value="waiting">Waiting</option>
                                    <option value="in-progress">In Progress</option>
                                    <option value="completed">Completed</option>
                                    <option value="archived">Archived</option>
                                </select>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Date From
                                </label>
                                <input
                                    type="date"
                                    value={filters.dateFrom}
                                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Date To
                                </label>
                                <input
                                    type="date"
                                    value={filters.dateTo}
                                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Has Appointments
                                </label>
                                <select
                                    value={filters.hasAppointments}
                                    onChange={(e) => handleFilterChange('hasAppointments', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All Patients</option>
                                    <option value="yes">Has Appointments</option>
                                    <option value="no">No Appointments</option>
                                </select>
                            </div>
                        </div>
                        
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Symptoms
                            </label>
                            <input
                                type="text"
                                placeholder="Search by symptoms..."
                                value={filters.symptoms}
                                onChange={(e) => handleFilterChange('symptoms', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>
                )}

                {/* Search Controls */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleSearch}
                            disabled={loading}
                            className="bg-blue-500 text-white px-6 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600 disabled:opacity-50"
                        >
                            <Search className="w-4 h-4" />
                            {loading ? 'Searching...' : 'Search'}
                        </button>
                        
                        <button
                            onClick={clearFilters}
                            className="bg-gray-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-600"
                        >
                            <X className="w-4 h-4" />
                            Clear
                        </button>
                    </div>
                    
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700">Sort by:</label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="name">Name</option>
                                <option value="age">Age</option>
                                <option value="createdAt">Created Date</option>
                                <option value="status">Status</option>
                            </select>
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700">Order:</label>
                            <select
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                className="px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="asc">Ascending</option>
                                <option value="desc">Descending</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search Results */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-6 border-b">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Search Results ({totalResults})
                        </h3>
                        {searchResults.length > 0 && (
                            <div className="text-sm text-gray-600">
                                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalResults)} of {totalResults}
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            <span className="ml-3 text-gray-600">Searching...</span>
                        </div>
                    ) : searchResults.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                            <p>No patients found matching your criteria</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {searchResults.map((patient) => (
                                <div key={patient.id} className="border rounded-lg p-4 hover:bg-gray-50">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h4 className="font-semibold text-gray-900">{patient.name}</h4>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(patient.status)}`}>
                                                    {patient.status}
                                                </span>
                                            </div>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                                                <div className="flex items-center gap-2">
                                                    <Clock className="w-4 h-4" />
                                                    {patient.age ? `${patient.age} years` : 'Age not specified'}
                                                </div>
                                                
                                                {patient.phone && (
                                                    <div className="flex items-center gap-2">
                                                        <Phone className="w-4 h-4" />
                                                        {patient.phone}
                                                    </div>
                                                )}
                                                
                                                {patient.email && (
                                                    <div className="flex items-center gap-2">
                                                        <Mail className="w-4 h-4" />
                                                        {patient.email}
                                                    </div>
                                                )}
                                                
                                                {patient.department && (
                                                    <div className="flex items-center gap-2">
                                                        <FileText className="w-4 h-4" />
                                                        {patient.department}
                                                    </div>
                                                )}
                                                
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="w-4 h-4" />
                                                    Created: {formatDate(patient.createdAt)}
                                                </div>
                                                
                                                {patient.symptoms && (
                                                    <div className="flex items-center gap-2 md:col-span-2">
                                                        <AlertTriangle className="w-4 h-4" />
                                                        {patient.symptoms.substring(0, 100)}...
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-2 ml-4">
                                            <button
                                                onClick={() => onNavigate('patients', { patientId: patient.id })}
                                                className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm hover:bg-blue-200"
                                            >
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="p-6 border-t">
                        <div className="flex justify-center items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`px-3 py-2 border rounded-lg ${
                                        currentPage === page 
                                            ? 'bg-blue-500 text-white border-blue-500' 
                                            : 'border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {page}
                                </button>
                            ))}
                            
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdvancedPatientSearch;
